import React from "react";
import type { ChangeEvent, FormEvent } from "react";
import {
  Button,
  Modal,
  Col,
  InputGroup,
  InputGroupText,
  Input,
  FormGroup,
  Form,
} from "reactstrap";
import classnames from "classnames";

type PasswordModalViewProps = {
  defaultModal: boolean;
  toggleModal: () => void;
  currentPassword: string;
  password: string;
  confirmPassword: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
  mandatory?: boolean;
  submitting?: boolean;
};

const PasswordModalView = ({
  defaultModal,
  toggleModal,
  currentPassword,
  password,
  confirmPassword,
  onChange,
  onSubmit,
  mandatory = false,
  submitting = false,
}: PasswordModalViewProps) => {
  const isOpen = mandatory ? true : defaultModal;

  return (
    <>
      {!mandatory && (
        <Button
          className="btn-neutral btn-icon"
          color="default"
          onClick={toggleModal}
        >
          <span className="btn-inner--icon">
            <i className="fas fa-key mr-2" />
          </span>
          <span className="nav-link-inner--text">Update Password</span>
        </Button>
      )}

      <Modal
        className="modal-dialog-centered"
        isOpen={isOpen}
        toggle={mandatory ? undefined : toggleModal}
        backdrop={mandatory ? "static" : true}
        keyboard={!mandatory}
      >
        <Form role="form" onSubmit={onSubmit}>
          <div className="modal-header">
            <h6 className="modal-title" id="modal-title-default">
              {mandatory ? "Change your password" : "Updating Your Password"}
            </h6>
            {!mandatory && (
              <button
                aria-label="Close"
                className="close"
                data-dismiss="modal"
                type="button"
                onClick={toggleModal}
              >
                <span aria-hidden={true}>×</span>
              </button>
            )}
          </div>

          <div className="modal-body">
            <FormGroup>
              <p>
                {mandatory
                  ? "Your account is still using the default password assigned at registration. Set a new password before continuing."
                  : "We suggest that you change your password for the security of your session."}
              </p>
            </FormGroup>

            <FormGroup>
              <Col md="10">
                <InputGroup className={classnames("input-group-merge")}>
                  <InputGroupText>
                    <i className="fas fa-lock" />
                  </InputGroupText>
                  <Input
                    placeholder="Current Password"
                    name="currentPassword"
                    type="password"
                    autoComplete="current-password"
                    value={currentPassword}
                    onChange={onChange}
                    disabled={submitting}
                    autoFocus={mandatory}
                  />
                </InputGroup>
              </Col>
            </FormGroup>

            <FormGroup>
              <Col md="10">
                <InputGroup className={classnames("input-group-merge")}>
                  <InputGroupText>
                    <i className="fas fa-key" />
                  </InputGroupText>
                  <Input
                    placeholder="Insert Password"
                    name="password"
                    type="password"
                    value={password}
                    onChange={onChange}
                    disabled={submitting}
                  />
                </InputGroup>
              </Col>
            </FormGroup>

            <FormGroup>
              <Col md="10">
                <InputGroup className={classnames("input-group-merge")}>
                  <InputGroupText>
                    <i className="fas fa-key" />
                  </InputGroupText>
                  <Input
                    placeholder="Retype Your Password"
                    name="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={onChange}
                    disabled={submitting}
                  />
                </InputGroup>
              </Col>
            </FormGroup>
          </div>

          <div className="modal-footer">
            <Button color="primary" type="submit" disabled={submitting}>
              {submitting ? "Saving..." : "Save changes"}
            </Button>

            {!mandatory && (
              <Button
                className="ml-auto"
                color="link"
                data-dismiss="modal"
                type="button"
                onClick={toggleModal}
              >
                Close
              </Button>
            )}
          </div>
        </Form>
      </Modal>
    </>
  );
};

export default PasswordModalView;
